import _get from 'lodash/get'
import { z } from 'zod'

import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import { zipCodeValidatorHookCallback } from '@flows/configs/shared/validatorHooks'
import * as validators from '@flows/configs/shared/validators'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_LiabilityV1GetsafeDe } from '@flows/insurance/schemas'

const claimsOptionsFromSchema = schemaUtils.getEnumValuesFromPipelineSchema(
  BaseSchema_LiabilityV1GetsafeDe.shape.number_of_claims_in_last_5y.unwrap()
)

const liabilityInstantSchema = (
  params: StepConfigParams,
  getParamPath: StepConfigParams['helperFunctions']['getParamPath']
) =>
  schemaUtils
    .generateZodObjectFromPaths({
      [getParamPath('previous_insurance_in_last_5y')]:
        BaseSchema_LiabilityV1GetsafeDe.shape.previous_insurance_in_last_5y,
      [getParamPath('family_coverage')]: BaseSchema_LiabilityV1GetsafeDe.shape.family_coverage,
      [getParamPath('number_of_claims_in_last_5y')]: validators
        .createPreprocessedEnumNumberSchema(z.enum([...claimsOptionsFromSchema, '3']))
        .optional(),
      zip_code: BaseSchema_LiabilityV1GetsafeDe.shape.zip_code,
    })
    .superRefine((values, ctx) => {
      if (_get(values, getParamPath('previous_insurance_in_last_5y')) === true) {
        if (typeof _get(values, getParamPath('number_of_claims_in_last_5y')) === 'undefined') {
          ctx.addIssue({
            path: [getParamPath('number_of_claims_in_last_5y')],
            code: z.ZodIssueCode.invalid_type,
            expected: 'integer',
            received: 'undefined',
            message: 'This field is required',
          })
        }
      }
    })

const liability_instant = (params: StepConfigParams) => {
  const getParamPath = params.helperFunctions.getParamPath
  return {
    schema: liabilityInstantSchema(params, getParamPath),
    stepContent: [
      {
        type: 'harmonyTitle',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_liabilityNxt:steps.liability.instant_step.title',
        },
        fontSize: 'primary',
      },
      {
        type: 'spacer',
        size: 'xsmall',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_liabilityNxt:steps.liability.instant_step.family_question',
        },
        infoButton: {
          modalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.family_coverage.modal.title',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.family_coverage.modal.text1',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.family_coverage.modal.text2',
              },
            },
            {
              type: 'customComponent',
              name: 'LiabilityFamilyCoverageInfoAccordions',
              props: {
                disabledIfValueExists: true,
                shouldTrackValues: true,
              },
              inputs: [],
              outputs: [],
            },
          ],
        },
      },
      {
        type: 'choice',
        as: 'chip',
        name: getParamPath('family_coverage'),
        options: [
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_liabilityNxt:steps.liability.instant_step.family.me',
            },
            value: false,
          },
          {
            label: {
              type: 'translatableText',
              translationKey: 'sharedFlows_liabilityNxt:steps.liability.instant_step.family.all',
            },
            value: true,
          },
        ],
        isOptional: false,
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_liabilityNxt:steps.liability.instant_step.zip_question',
        },
        infoButton: {
          modalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.zip.modal.title',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.zip.modal.text1',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.zip.modal.text2',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.zip.modal.text3',
              },
            },
          ],
        },
      },
      {
        type: 'input',
        name: 'zip_code',
        styleType: 'outlined',
        inputType: 'tel',
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_liabilityNxt:steps.liability.instant_step.zip.input.label',
        },
      },
      {
        type: 'spacer',
        size: 'mini',
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey:
            'sharedFlows_liabilityNxt:steps.liability.instant_step.previous_insurance',
        },
        infoButton: {
          modalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.claims.modal.title',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.claims.modal.text1',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_liabilityNxt:steps.liability.instant_step.claims.modal.text2',
              },
            },
          ],
        },
      },
      {
        type: 'customComponent',
        name: 'LiabilityInstantQuoteQuestions',
        props: {
          disabledIfValueExists: true,
          shouldTrackValues: true,
        },
        inputs: [],
        outputs: [
          getParamPath('previous_insurance_in_last_5y'),
          getParamPath('number_of_claims_in_last_5y'),
        ],
      },
    ],
    subFlow: params.subFlow,
    onSubmit: {
      type: 'simpleHook',
      inputs: [getParamPath('previous_insurance_in_last_5y')],
      outputs: [
        getParamPath('number_of_claims_in_last_5y'),
        getParamPath('previous_insurance_in_last_5y'),
      ],
      callback: async (flowValues: FlowValues) => {
        const zipCodeValidationError = await zipCodeValidatorHookCallback(params)(flowValues)

        if (zipCodeValidationError) {
          return zipCodeValidationError
        }

        const prevInsurance = _get(flowValues, getParamPath('previous_insurance_in_last_5y'))

        if (prevInsurance === false) {
          return {
            formValues: {
              [getParamPath('number_of_claims_in_last_5y')]: 0,
              [getParamPath('cancelled_by_previous_insurer')]: false,
            },
          }
        }
        return {
          formValues: {
            [getParamPath('cancelled_by_previous_insurer')]: false,
          },
        }
      },
    },
    nextStep: 'liability_quote',
  } as const satisfies AnyStepConfiguration
}

export default liability_instant
